/**
 * Countdown — refreshed dashboard countdown card.
 *
 * Replaces the legacy small-text countdown row with a typographic
 * hero treatment that fits the rest of the system:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ Pageant Day                                  │  ← eyebrow
 *   │ March 15, 2026                               │  ← page-title-sm
 *   │ ──────────────────────────────────────────── │
 *   │  127        11        42        18           │  ← text-kpi-lg italic
 *   │  DAYS    │  HRS    │  MIN   │  SEC          │  ← eyebrow-sm
 *   └──────────────────────────────────────────────┘
 *
 * The numerals carry the canonical KPI italic-display treatment
 * (slate-teal italic Bavex) and the unit labels carry the eyebrow
 * treatment (uppercase + tracking + muted). Single source of truth
 * for "how a countdown should look" in the system; both the
 * contestant and admin dashboards consume this primitive.
 *
 * Time computation
 * ----------------
 * Calling code passes a `targetDate` (UTC ISO timestamp). The
 * component computes the remaining duration, splits into days /
 * hours / minutes / seconds, and refreshes once per second via
 * `setInterval`. After the target passes, all four cells render
 * `0` and an optional `pastLabel` prop replaces the eyebrow.
 *
 * Spec: meridian-redesign — PR (m.6) Phase 1 (refreshed for m.7)
 * Requirements: 1.5, 14.1, 18.5
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { cn } from '@/lib/utils';

export interface CountdownProps {
  /** Target date — ISO 8601. */
  targetDate: string;
  /** Eyebrow label rendered above the date. Defaults to "Pageant Day". */
  eyebrow?: string;
  /** Eyebrow override after the target passes. Defaults to "Pageant complete". */
  pastEyebrow?: string;
  /** Display variant — `card` (default, full chrome) or `bare` (just the number row). */
  variant?: 'card' | 'bare';
  /** Extra classes merged via `cn()`. */
  className?: string;
  /** Stable test id; defaults to `countdown`. */
  'data-testid'?: string;
}

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

function computeRemaining(target: number, now: number): Remaining {
  const ms = Math.max(0, target - now);
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, isPast: ms === 0 };
}

const FRIENDLY_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const UNITS: ReadonlyArray<{ key: keyof Omit<Remaining, 'isPast'>; label: string }> = [
  { key: 'days', label: 'Days' },
  { key: 'hours', label: 'Hours' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'seconds', label: 'Seconds' },
];

export function Countdown({
  targetDate,
  eyebrow = 'Pageant Day',
  pastEyebrow = 'Pageant Complete',
  variant = 'card',
  className,
  'data-testid': dataTestId,
}: CountdownProps): JSX.Element {
  const targetMs = useMemo(() => new Date(targetDate).getTime(), [targetDate]);
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = useMemo(() => computeRemaining(targetMs, now), [targetMs, now]);
  const friendlyDate = useMemo(
    () => FRIENDLY_DATE.format(new Date(targetMs)),
    [targetMs],
  );

  const numbersRow = (
    <div
      data-testid="countdown-numbers"
      className="grid grid-cols-4 gap-3 sm:gap-6"
    >
      {UNITS.map((unit) => (
        <div key={unit.key} className="flex flex-col items-center text-center">
          <span
            data-testid={`countdown-${unit.key}`}
            className="font-display text-kpi sm:text-kpi-lg text-tertiary"
          >
            {String(remaining[unit.key]).padStart(2, '0')}
          </span>
          <span className="mt-1 text-eyebrow-sm text-muted-foreground">
            {unit.label}
          </span>
        </div>
      ))}
    </div>
  );

  if (variant === 'bare') {
    return (
      <div data-testid={dataTestId ?? 'countdown'} className={className}>
        {numbersRow}
      </div>
    );
  }

  return (
    <section
      data-testid={dataTestId ?? 'countdown'}
      data-countdown-past={remaining.isPast || undefined}
      aria-label={`${remaining.isPast ? pastEyebrow : eyebrow}: ${friendlyDate}`}
      className={cn(
        'rounded-card border border-border bg-card shadow-subtle overflow-hidden',
        className,
      )}
    >
      <div className="flex flex-col gap-1 p-card-comfortable sm:p-card-roomy bg-muted/50">
        <span className="text-eyebrow-sm text-muted-foreground">
          {remaining.isPast ? pastEyebrow : eyebrow}
        </span>
        <p className="font-display text-page-title text-foreground ">
          {friendlyDate}
        </p>
      </div>
      <div className="border-t border-border p-card-comfortable sm:p-card-roomy">{numbersRow}</div>
    </section>
  );
}

export default Countdown;
